import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ServicePackage } from '../../types/service';
import { 
  XMarkIcon, CheckIcon, ClockIcon, ArrowPathIcon, CalendarIcon, 
  MapPinIcon, UsersIcon, VideoCameraIcon, StarIcon, ExclamationTriangleIcon,
  CreditCardIcon, PhotoIcon, CheckCircleIcon, ChatBubbleLeftIcon
} from '@heroicons/react/24/outline';
import Button from '../common/Button';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../utils/format';
import { resolveImageUrl } from '../../utils/image';
import { collaborationAPI } from '../../services/api';
import RequestDetailModal from '../collaboration/RequestDetailModal';

interface ServicePackageDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  packageData: ServicePackage | null;
  isOwner: boolean;
  onEdit?: (pkg: ServicePackage) => void;
  onDelete?: (pkg: ServicePackage) => void;
  onBook?: (pkg: ServicePackage) => void;
  zIndex?: number;
}

const ServicePackageDetailsModal: React.FC<ServicePackageDetailsModalProps> = ({
  isOpen,
  onClose,
  packageData,
  isOwner,
  onEdit,
  onDelete,
  onBook,
  zIndex,
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'info' | 'analytics'>('info');
  const [requests, setRequests] = useState<any[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(null);
  const [managingRequest, setManagingRequest] = useState<any | null>(null);

  useEffect(() => {
    if (isOpen && packageData && isOwner) {
      setLoadingRequests(true);
      console.log('[PackageAnalytics] Fetching requests for package:', packageData.id, packageData.title);
      collaborationAPI.getCollaborationRequests({
        service_package_id: packageData.id,
        limit: 200,
      })
      .then(res => {
        console.log('[PackageAnalytics] Raw API response:', res.data);
        if (res.data?.success) {
          // SuccessResponse wraps: { success, data: { data: { sentRequests, receivedRequests }, pagination } }
          // So the actual lists are at res.data.data.data
          const inner = res.data.data?.data || res.data.data || {};
          const sent = inner?.sentRequests || [];
          const received = inner?.receivedRequests || [];
          console.log('[PackageAnalytics] sentRequests:', sent.length, 'receivedRequests:', received.length);
          const combined = [...sent, ...received];
          // De-duplicate by request ID
          const uniqueRequests = Array.from(
            new Map(combined.map(item => [item.id, item])).values()
          );
          console.log('[PackageAnalytics] Combined unique requests:', uniqueRequests.length);
          uniqueRequests.forEach(r => {
            console.log('[PackageAnalytics] Request:', r.id, '| service_package_id:', r.service_package_id, '| package id:', packageData.id, '| match:', r.service_package_id === packageData.id);
          });
          // Filter client-side to only requests linked to this package (backend filter is also active)
          const packageRequests = uniqueRequests.filter(r => r.service_package_id === packageData.id);
          console.log('[PackageAnalytics] Filtered requests for this package:', packageRequests.length);
          setRequests(packageRequests);
        }
      })
      .catch(err => {
        console.error("Failed to fetch requests for package:", err);
      })
      .finally(() => {
        setLoadingRequests(false);
      });
    } else {
      setActiveTab('info');
      setRequests([]);
      setFilterStatus('all');
      setExpandedRequestId(null);
    }
  }, [isOpen, packageData?.id, isOwner]);

  if (!isOpen || !packageData) return null;

  const handleFolderClick = (folderId: string) => {
    // Route pattern: /profile/:id/portfolio/folders/:folder_id
    navigate(`/profile/${packageData.creator_id}/portfolio/folders/${folderId}`);
    onClose();
  };

  const modalRoot = document.getElementById('modal-root');
  if (!modalRoot) return null;

  const getCategoryColor = (category: string) => {
    const normalized = category.toLowerCase();
    if (normalized.includes('wedding')) return 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800';
    if (normalized.includes('fashion') || normalized.includes('portrait')) return 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800';
    if (normalized.includes('commercial') || normalized.includes('product')) return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800';
    if (normalized.includes('event')) return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800';
    if (normalized.includes('baby') || normalized.includes('maternity')) return 'bg-pink-100 text-pink-800 border-pink-200 dark:bg-pink-900/30 dark:text-pink-300 dark:border-pink-800';
    return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
  };

  const images = packageData.sample_images_signed?.length ? packageData.sample_images_signed : packageData.sample_images;
  const hasPhotos = images && images.length > 0;
  const coverPhoto = hasPhotos ? resolveImageUrl(images[0]) : null;
  const galleryPhotos = hasPhotos ? images.slice(1) : [];

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const acceptedRequests = requests.filter(r => r.status === 'accepted' || r.status === 'completed');
  const earnedAmount = acceptedRequests.reduce((sum, r) => sum + (r.budget || 0), 0);

  const filteredRequests = requests
    .filter(r => {
      if (filterStatus === 'all') return true;
      if (filterStatus === 'pending') return r.status === 'pending';
      if (filterStatus === 'accepted') return r.status === 'accepted' || r.status === 'completed';
      if (filterStatus === 'declined') return r.status === 'declined';
      return true;
    })
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const toggleExpandRequest = (id: string) => {
    setExpandedRequestId(expandedRequestId === id ? null : id);
  };

  const renderAnalyticsTab = () => {
    const pendingCount = pendingRequests.length;
    const acceptedCount = acceptedRequests.length;

    return (
      <div className="space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div 
            onClick={() => setFilterStatus('all')}
            className={`cursor-pointer p-4 rounded-2xl border transition-all duration-300 ${
              filterStatus === 'all' 
                ? 'border-blue-500 bg-blue-50/30 dark:bg-blue-900/10 shadow-md scale-102' 
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-800'
            }`}
          >
            <div className="text-2xl mb-1">📊</div>
            <p className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider">Total Requests</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">{requests.length}</p>
          </div>

          <div 
            onClick={() => setFilterStatus('pending')}
            className={`cursor-pointer p-4 rounded-2xl border transition-all duration-300 ${
              filterStatus === 'pending' 
                ? 'border-yellow-500 bg-yellow-50/30 dark:bg-yellow-900/10 shadow-md scale-102' 
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-yellow-300 dark:hover:border-yellow-800'
            }`}
          >
            <div className="text-2xl mb-1">⏳</div>
            <p className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider">Pending</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">{pendingCount}</p>
          </div>

          <div 
            onClick={() => setFilterStatus('accepted')}
            className={`cursor-pointer p-4 rounded-2xl border transition-all duration-300 ${
              filterStatus === 'accepted' 
                ? 'border-green-500 bg-green-50/30 dark:bg-green-900/10 shadow-md scale-102' 
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-green-300 dark:hover:border-green-800'
            }`}
          >
            <div className="text-2xl mb-1">✅</div>
            <p className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider">Completed / Accepted</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">{acceptedCount}</p>
          </div>

          <div 
            className="p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
          >
            <div className="text-2xl mb-1">💰</div>
            <p className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider">Amount Earned</p>
            <p className="text-xl font-bold text-green-600 dark:text-green-400 mt-1">{formatCurrency(earnedAmount)}</p>
          </div>
        </div>

        {/* Requests List */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-gray-900 dark:text-white uppercase tracking-wider">
              {filterStatus === 'all' && 'All Requests'}
              {filterStatus === 'pending' && 'Pending Requests'}
              {filterStatus === 'accepted' && 'Accepted Requests'}
              {filterStatus === 'declined' && 'Declined Requests'}
            </h3>
            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium bg-gray-100 dark:bg-gray-800 px-2.5 py-1 rounded-full">
              {filteredRequests.length} Item(s)
            </span>
          </div>

          {loadingRequests ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
              <p className="text-gray-500 dark:text-gray-400">No requests found matching this status filter.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredRequests.map((req) => {
                const senderName = `${req.sender?.first_name || 'Client'} ${req.sender?.last_name || ''}`.trim();
                const initials = (req.sender?.first_name?.[0] || 'C') + (req.sender?.last_name?.[0] || '');
                const sc: Record<string, { bar: string; badge: string }> = {
                  pending:   { bar: 'bg-amber-400',   badge: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800/30' },
                  accepted:  { bar: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800/30' },
                  completed: { bar: 'bg-emerald-600', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800/30 font-bold' },
                  declined:  { bar: 'bg-rose-500',    badge: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-800/30' },
                  cancelled: { bar: 'bg-gray-400',    badge: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600' },
                };
                const { bar, badge } = sc[req.status] || sc.cancelled;

                return (
                  <div key={req.id} className="relative flex items-center gap-3 bg-white dark:bg-gray-800/80 border border-gray-100 dark:border-gray-700/50 rounded-xl overflow-hidden hover:shadow-md transition-all duration-200 pl-3 pr-4 py-2.5">
                    {/* Status accent bar */}
                    <div className={`absolute left-0 top-0 bottom-0 w-1 ${bar}`} />

                    {/* Avatar */}
                    <div className="h-8 w-8 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0 ml-1">
                      {req.sender?.profile_pic_signed || req.sender?.profile_pic ? (
                        <img src={resolveImageUrl(req.sender?.profile_pic_signed || req.sender?.profile_pic)} alt={senderName} className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-xs font-bold text-gray-500 dark:text-gray-300">{initials}</div>
                      )}
                    </div>

                    {/* Main content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-gray-900 dark:text-white truncate">{senderName}</span>
                        {req.budget > 0 && <span className="text-xs font-bold text-gray-700 dark:text-gray-200 flex-shrink-0">₹{req.budget?.toLocaleString('en-IN')}</span>}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[11px] text-gray-400">{new Date(req.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
                        {req.preferred_dates?.[0] && (
                          <span className="inline-flex items-center gap-0.5 text-[11px] text-gray-400">
                            <CalendarIcon className="h-2.5 w-2.5" />{req.preferred_dates[0]}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right: status + manage */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border ${badge}`}>
                        {req.status}
                      </span>
                      <button
                        onClick={() => setManagingRequest(req)}
                        className="text-[11px] font-semibold text-orange-600 dark:text-orange-400 hover:text-orange-700 bg-orange-50 dark:bg-orange-500/10 hover:bg-orange-100 dark:hover:bg-orange-500/20 px-2.5 py-1 rounded-lg transition-all"
                      >
                        Manage
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const mainModal = createPortal(
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn"
      style={{ zIndex: zIndex || 10000 }}
    >
      <div 
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-200 dark:border-gray-800 animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Minimalist Header */}
        <div className="relative sticky top-0 flex-shrink-0 z-10 w-full bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 px-6 py-5">
          <button 
            onClick={onClose} 
            className="absolute top-5 right-5 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
          
          <div className="pr-12">
            <div className="flex justify-between items-start mb-4">
              <span className="inline-block px-2.5 py-1 rounded-md text-[10px] font-bold bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300 uppercase tracking-wide">
                {packageData.category || 'Package'}
              </span>
              <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold tracking-wide shadow-sm">
                 {formatCurrency(packageData.price)}
              </span>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">
              {packageData.title}
            </h2>
          </div>
        </div>

        {/* Tab Bar (Only for Package Owner) */}
        {isOwner && (
          <div className="flex border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 px-6 shrink-0">
            <button
              onClick={() => setActiveTab('info')}
              className={`py-3 px-4 font-semibold text-sm border-b-2 transition-colors ${
                activeTab === 'info'
                  ? 'border-orange-500 text-orange-500 font-bold'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Package Info
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-3 px-4 font-semibold text-sm border-b-2 transition-colors ${
                activeTab === 'analytics'
                  ? 'border-orange-500 text-orange-500 font-bold'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Analytics & Requests
            </button>
          </div>
        )}

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto w-full p-6 bg-gray-50 dark:bg-gray-900/50">
          {activeTab === 'analytics' ? renderAnalyticsTab() : (
            <div className="space-y-8">
              {/* Cover Photo */}
              {coverPhoto && (
            <div className="w-full h-48 sm:h-64 rounded-2xl overflow-hidden shadow-sm border border-gray-200 dark:border-gray-800 relative">
              <img src={coverPhoto} alt="Package Cover" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            </div>
          )}

          {/* Quick Stats Banner */}
          <div className="grid grid-cols-3 gap-3">
             <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-200 dark:border-gray-700 flex flex-col items-center text-center justify-center shadow-sm">
               <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 dark:text-orange-400 mb-2">
                 <ClockIcon className="w-4 h-4" />
               </div>
               <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-0.5">Duration</span>
               <span className="text-sm font-bold text-gray-900 dark:text-white">{packageData.session_duration_hours} hrs / {packageData.number_of_days} days</span>
             </div>
             
             <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-200 dark:border-gray-700 flex flex-col items-center text-center justify-center shadow-sm">
               <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 mb-2">
                 <UsersIcon className="w-4 h-4" />
               </div>
               <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-0.5">Team Size</span>
               <span className="text-sm font-bold text-gray-900 dark:text-white">{packageData.team_size} Person(s)</span>
             </div>

             <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-200 dark:border-gray-700 flex flex-col items-center text-center justify-center shadow-sm">
               <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400 mb-2">
                 <ArrowPathIcon className="w-4 h-4" />
               </div>
               <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-0.5">Revisions</span>
               <span className="text-sm font-bold text-gray-900 dark:text-white">{packageData.revisions} Time(s)</span>
             </div>
          </div>

          {/* About */}
          {packageData.description && (
            <div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2 uppercase tracking-wide">About this Package</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                {packageData.description}
              </p>
            </div>
          )}

          {/* Details Grid */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
            <div className="p-4 sm:p-5 border-b border-gray-100 dark:border-gray-700">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide">Logistics & Details</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-gray-100 dark:bg-gray-700">
              <div className="bg-white dark:bg-gray-800 p-4 sm:p-5">
                <span className="text-xs text-gray-400 font-bold uppercase tracking-wider block mb-1">Shoot Type</span>
                <span className="text-sm font-medium text-gray-800 dark:text-gray-200 capitalize">{packageData.shoot_type || 'Any'} Shoot</span>
              </div>
              <div className="bg-white dark:bg-gray-800 p-4 sm:p-5">
                <span className="text-xs text-gray-400 font-bold uppercase tracking-wider block mb-1">Look(s)</span>
                <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{packageData.number_of_looks || 1} Look(s) included</span>
              </div>
              <div className="bg-white dark:bg-gray-800 p-4 sm:p-5">
                <span className="text-xs text-gray-400 font-bold uppercase tracking-wider block mb-1">Locations</span>
                {packageData.locations && packageData.locations.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {packageData.locations.map((loc, i) => (
                      <span key={i} className="inline-flex items-center px-2 py-1 rounded bg-gray-50 dark:bg-gray-700 text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                        <MapPinIcon className="w-3 h-3 mr-1" /> {loc}
                      </span>
                    ))}
                  </div>
                ) : <span className="text-sm text-gray-500">Not specified</span>}
              </div>
              <div className="bg-white dark:bg-gray-800 p-4 sm:p-5">
                <span className="text-xs text-gray-400 font-bold uppercase tracking-wider block mb-1">Delivery Format</span>
                <span className="text-sm font-medium text-gray-800 dark:text-gray-200 capitalize">{packageData.delivery_format?.replace('_', ' ') || 'Digital'}</span>
              </div>
            </div>
          </div>

          {/* Conditional Alerts (Travel / Advance) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {packageData.advance_percentage > 0 && (
              <div className="bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-900/30 rounded-xl p-4 flex gap-3 items-center">
                <div className="bg-white dark:bg-gray-800 p-2 rounded-full shadow-sm">
                  <CreditCardIcon className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <span className="text-[11px] font-bold text-orange-800 dark:text-orange-400 uppercase tracking-wider block mb-0.5">Advance Payment</span>
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{packageData.advance_percentage}% upfront required to secure booking</span>
                </div>
              </div>
            )}
            
            {packageData.travel_included && (
              <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-900/30 rounded-xl p-4 flex gap-3 items-center">
                <div className="bg-white dark:bg-gray-800 p-2 rounded-full shadow-sm text-blue-500 text-lg flex items-center justify-center">
                  🚗
                </div>
                <div>
                  <span className="text-[11px] font-bold text-blue-800 dark:text-blue-400 uppercase tracking-wider block mb-0.5">Travel Included</span>
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Up to {packageData.max_travel_km} km limit within perimeter</span>
                </div>
              </div>
            )}
          </div>

          {/* Output & Features */}
          <div className="space-y-6">
            <div>
               <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3 uppercase tracking-wide">Included in this package</h3>
               <div className="flex flex-wrap gap-2 mb-4">
                 {packageData.number_of_edited_photos > 0 && (
                   <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm font-bold text-gray-800 dark:text-gray-200 shadow-sm">
                     <PhotoIcon className="w-4 h-4 mr-1.5 text-gray-400" /> {packageData.number_of_edited_photos} Edited Photos
                   </span>
                 )}
                 {packageData.number_of_raw_photos > 0 && (
                   <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm font-bold text-gray-800 dark:text-gray-200 shadow-sm">
                     <PhotoIcon className="w-4 h-4 mr-1.5 text-gray-400" /> {packageData.number_of_raw_photos} Raw Photos
                   </span>
                 )}
                 {packageData.video_included && (
                   <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm font-bold text-gray-800 dark:text-gray-200 shadow-sm">
                     <VideoCameraIcon className="w-4 h-4 mr-1.5 text-gray-400" /> {packageData.video_duration_seconds}s Video / Reel
                   </span>
                 )}
                 {packageData.delivery_time_days > 0 && (
                   <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm font-bold text-gray-800 dark:text-gray-200 shadow-sm">
                     ⏳ {packageData.delivery_time_days} days delivery
                   </span>
                 )}
               </div>

               {/* Inclusions & Perks List */}
               {(packageData.inclusions?.length > 0 || packageData.perks?.length > 0) && (
                 <div className="bg-green-50/50 dark:bg-green-900/10 border border-green-100 dark:border-green-900/30 rounded-2xl p-5 mt-4">
                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                     {packageData.inclusions?.map((item, index) => (
                       <div key={index} className="flex items-start">
                         <div className="bg-green-100 dark:bg-green-900/50 rounded-full p-0.5 mt-0.5 mr-2.5">
                           <CheckIcon className="h-3 w-3 text-green-600 dark:text-green-400" />
                         </div>
                         <span className="text-sm font-medium text-gray-700 dark:text-gray-300 leading-snug">{item}</span>
                       </div>
                     ))}
                     {packageData.perks?.map((perk, index) => (
                       <div key={`perk-${index}`} className="flex items-start">
                         <div className="bg-yellow-100 dark:bg-yellow-900/50 rounded-full p-0.5 mt-0.5 mr-2.5">
                           <StarIcon className="h-3 w-3 text-yellow-600 dark:text-yellow-400" />
                         </div>
                         <span className="text-sm font-medium text-gray-700 dark:text-gray-300 leading-snug">{perk}</span>
                       </div>
                     ))}
                   </div>
                 </div>
               )}
            </div>

            {/* Exclusions */}
            {packageData.exclusions && packageData.exclusions.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-gray-500 mb-3 uppercase tracking-wide">Not Included</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {packageData.exclusions.map((item, index) => (
                    <div key={index} className="flex items-start text-gray-500 dark:text-gray-500 opacity-75">
                      <XMarkIcon className="h-4 w-4 mr-2 text-red-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sm font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sample Gallery Grid */}
          {(galleryPhotos.length > 0 || (packageData.portfolio_folders_data && packageData.portfolio_folders_data.length > 0)) && (
            <div className="space-y-6">
               <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3 uppercase tracking-wide">Sample Work</h3>
               
               {/* Folders first if any */}
               {packageData.portfolio_folders_data && packageData.portfolio_folders_data.length > 0 && (
                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                   {packageData.portfolio_folders_data.map((folder) => (
                     <button
                       key={folder.id}
                       onClick={() => handleFolderClick(folder.id)}
                       className="group flex items-center gap-3 p-3 rounded-2xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800 hover:border-orange-300 dark:hover:border-orange-500/50 transition-all text-left"
                     >
                       <div className="h-14 w-14 shrink-0 rounded-xl overflow-hidden bg-gray-200 dark:bg-gray-700 relative">
                         {folder.thumbnail_image_url_signed ? (
                           <img src={resolveImageUrl(folder.thumbnail_image_url_signed)} alt={folder.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform" />
                         ) : (
                           <div className="flex items-center justify-center h-full w-full text-2xl">📁</div>
                         )}
                       </div>
                       <div className="flex-1 overflow-hidden">
                         <h4 className="font-bold text-sm text-gray-900 dark:text-white truncate">{folder.name}</h4>
                         <p className="text-xs text-orange-500 font-semibold group-hover:translate-x-1 transition-transform inline-flex items-center">
                           View Folder <span className="ml-1">→</span>
                         </p>
                       </div>
                     </button>
                   ))}
                 </div>
               )}

               {/* Then individual photos */}
               {galleryPhotos.length > 0 && (
                 <div className="grid grid-cols-3 gap-3">
                   {galleryPhotos.map((url, i) => (
                     <div key={i} className="aspect-square rounded-xl overflow-hidden shadow-sm border border-gray-100 dark:border-gray-800">
                       <img src={resolveImageUrl(url)} alt={`Sample ${i+2}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                     </div>
                   ))}
                 </div>
               )}
            </div>
          )}
            
          {/* Footer Timeline */}
          <div className="flex items-center justify-between text-xs text-gray-400 dark:text-gray-500 pt-6 border-t border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-1.5">
              <CalendarIcon className="h-4 w-4" />
              <span>Created: {new Date(packageData.created_at).toLocaleDateString()}</span>
            </div>
            {packageData.requires_consultation && (
              <div className="flex items-center gap-1.5 text-orange-500 font-medium">
                <ExclamationTriangleIcon className="h-4 w-4" />
                <span>Consultation prior to booking</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>

        {/* Actions Footer */}
        {activeTab === 'info' && (isOwner || onBook) && (
          <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 p-5 flex-shrink-0">
          {isOwner ? (
            <div className="flex gap-3">
              <button
                onClick={() => {
                  onEdit?.(packageData);
                  onClose();
                }}
                className="flex-1 py-3 text-sm font-bold rounded-xl border-2 border-black dark:border-white bg-black dark:bg-white text-white dark:text-black hover:bg-transparent dark:hover:bg-transparent hover:text-black dark:hover:text-white transition-colors"
              >
                Edit Package
              </button>
              <button
                onClick={() => {
                  onDelete?.(packageData);
                  onClose();
                }}
                className="flex-1 py-3 text-sm font-bold rounded-xl border-2 border-red-200 dark:border-red-900 text-red-500 hover:bg-orange-500 hover:border-orange-500 hover:text-white dark:hover:bg-orange-500 dark:hover:border-orange-500 transition-colors"
              >
                Delete Package
              </button>
            </div>
          ) : onBook ? (
            <Button
              variant="primary"
              className="w-full py-4 text-base font-bold shadow-xl shadow-orange-500/10 hover:shadow-orange-500/20 bg-orange-500 hover:bg-orange-600 rounded-xl"
              onClick={() => {
                onBook(packageData);
                onClose();
              }}
            >
              Book Package • {formatCurrency(packageData.price)}
            </Button>
          ) : null}
          </div>
        )}
      </div>
    </div>,
    modalRoot
  );

  // Render RequestDetailModal for "Manage Request" action
  const requestDetailEl = managingRequest ? (
    <RequestDetailModal
      request={managingRequest}
      isReceived={true}
      onClose={() => setManagingRequest(null)}
      zIndex={10010}
      onResponse={async (requestId, status) => {
        try {
          await collaborationAPI.respondToCollaborationRequest(requestId, { status });
          // Refresh requests list
          const res = await collaborationAPI.getCollaborationRequests({ service_package_id: packageData?.id, limit: 200 });
          if (res.data?.success) {
            const inner = res.data.data?.data || res.data.data || {};
            const combined = [...(inner?.sentRequests || []), ...(inner?.receivedRequests || [])];
            const unique = Array.from(new Map(combined.map(r => [r.id, r])).values());
            setRequests(unique.filter(r => r.service_package_id === packageData?.id));
          }
        } catch (e) { /* handled by toast in modal */ }
        setManagingRequest(null);
      }}
    />
  ) : null;

  return (
    <>
      {mainModal}
      {requestDetailEl}
    </>
  );
};

export default ServicePackageDetailsModal;
